import { useCallback, useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
// Distinct fill cues per platform so the operator can tell by ear
// which platform just filled without looking at the row. Each fill
// event plays the clip ONCE (no loop); rapid bursts are throttled by
// EXECUTION_CLIP_MS so we never restart a clip mid-play. When both
// platforms fill in the same cycle the clips overlap on independent
// players — the "both sides are moving right now" signal.
import kalshiFillSound from 'src/Sounds/kalshi-fill.mp3';
import polyFillSound from 'src/Sounds/poly-fill.mp3';
// Distinct alert tone for errors / manual-intervention events.
// Re-uses the existing doorbell clip — was the previous full-fill
// sound, freed up when fills migrated to the cash register loop.
import alarmSound from 'src/Sounds/error.mp3';
// `counter_stuck` gets its OWN clip so a naked leg is tellable by ear from
// every other alarm — it is the one alarm that means "money is un-hedged
// right now", and the operator asked to stop guessing which of the four it
// was. Every other alarm keeps the shared doorbell.
import counterStuckSound from 'src/Sounds/counter_stuck.mp3';
import type {
  NotificationEvent,
  NotificationEventType
} from '../types/notifications.types';

/**
 * Two audio tiers for prediction-market notifications.
 *
 *   1. **Alarm** — a short whitelist (`ALARM_EVENT_TYPES` below), not
 *      "every error". There is NO conditional path into this tier any
 *      more: membership in the Set is the whole rule. Plays a doorbell
 *      tone (`counter_stuck` has its own clip AND its own playback lane
 *      — see `DEDICATED_ALARM_TYPE`),
 *      throttled by ALARM_COOLDOWN_MS so a flurry of related events
 *      doesn't blast the operator — read that constant's known limit
 *      before treating the tone as a count. Severity does NOT decide the
 *      tier: `counter_stuck` is a wire `warning` and rings; several wire
 *      `error`s do not.
 *
 *   2. **Execution** — every fill, partial fill, and cycle-complete
 *      event, regardless of origin: auto-unwinder, auto-opener
 *      (direction === 'entry'), AND manual operator orders. SPLIT BY
 *      PLATFORM:
 *      Kalshi fills play `kalshi-fill.mp3` on a dedicated burst-
 *      coalesced loop, Polymarket fills play `poly-fill.mp3` on a
 *      separate dedicated burst-coalesced loop. The two loops are
 *      independent and can overlap — when both platforms fill in
 *      the same cycle the operator hears both rhythms, which is the
 *      exact "both sides are moving" signal we want.
 *
 *      Manual fills (`manual_order_executed`) ride this same
 *      per-platform cue rather than a separate achievement chime — the
 *      operator wanted ONE consistent "platform X just filled" sound
 *      across every fill source, automation or manual. Per-chunk
 *      partials on a still-resting manual order
 *      (`manual_order_partial_fill`) ride the SAME cue too; the 🟡 chip
 *      is the visual differentiator, audio stays consistent so a
 *      partial chunk isn't silently dropped.
 *
 * Priority order (top wins): **alarm > execution**. Alarms punch
 * through a running fill loop as the only "stop and look" signal.
 *
 * Anything not in any set stays silent — the row, color, and Slack
 * post are still there, just no sound. Avoids alarm fatigue on
 * lifecycle / digest / recovery-success events. `tna_negative` is
 * deliberately silent too (sound removed 2026-06-09 by operator
 * request) — the rows + Slack post still surface every crossing.
 *
 * The alarm tier is the ONLY thing this hook makes audible besides
 * fills. Everything else the operator hears comes from page-scoped
 * hooks on a different socket namespace and is untouched by this file:
 * `useSniperReversalSound` (goal reversal, Sniper page),
 * `useVarReviewSound` (VAR started) and `useMatchEventSound`
 * (goal / corner). Silencing something here never silences those.
 */
// Fills route to the per-platform cash-register loop regardless of
// origin: auto-unwinder, auto-opener (direction === 'entry'), AND
// manual operator orders. The operator wants ONE consistent "platform X just filled"
// audio signal — no separate chime for manual fills, no silence for
// opener fills. `manual_order_placed` (the click ack) stays unlisted
// because the click itself already confirmed it.
// Base event types only — the entry-vs-unwind axis now lives on
// `event.direction`, not the type name, so a single base entry covers
// both the opener (buy-side) and unwinder (sell-side) fill of the same
// kind. The cue is identical for both directions anyway (the operator
// wants ONE "platform X just filled" signal regardless of side).
const EXECUTION_EVENT_TYPES = new Set<NotificationEventType>([
  // Full fills
  'maker_filled',
  'counter_filled',
  'recovery_filled',
  'crossing_executed',
  // Partial fills
  'maker_partial_fill',
  'partial_fill_accumulated',
  'crossing_partial',
  'crossing_accumulated',
  // Cycle done
  'execution_complete',
  // Manual operator-placed fill — operator wanted the same per-platform
  // fill sound as automation fills, no separate chime. Partial chunks on
  // a still-resting manual order ring the same per-platform cue as the
  // full fill — the 🟡 chip is the only thing that distinguishes them
  // visually, and the operator wants identical audio so a per-trade
  // partial isn't silent.
  'manual_order_executed',
  'manual_order_partial_fill',
  // Sniper single-FAK sweep landed — ride the same per-platform fill cue so
  // the operator hears "venue X just filled" on a live-event snipe too. The
  // auto-NO period-close tail is the same kind of event (a real GTC fill on the
  // NO side), so it rides the SAME cue — the operator must hear it fill too.
  'sniper_fired',
  'sniper_auto_no_fired',
  // A VAR all-clear rebuy — the goal was confirmed and we bought the position
  // back. A BUY fill, so it rides the execution cue (and is audibly distinct from
  // the annulment SELL exit, which alarms below).
  'sniper_var_rebuy',
  // The ladder's resting exit FILLING is a real fill — it rides the same cue.
  // Its placement/amend do not: on a rising confidence they fire repeatedly,
  // and a sound on every rung would train the operator to ignore the cue.
  'sniper_ladder_sell_filled',
  // Every booked BUY fill delta — partial chunks included. Same rationale as
  // `manual_order_partial_fill`: the operator hears "venue X just filled" for
  // each chunk; the filled-of-total on the row is the visual differentiator.
  'sniper_buy_filled'
]);

/**
 * The alarm tone is a DELIBERATELY SHORT whitelist, chosen by the operator.
 * `ALARM_EVENT_TYPES` below is the list; every entry carries its own reason
 * beside it, which is the only account of this Set that cannot drift. The
 * shape of the history: narrowed hard on 2026-08-07 and again 2026-08-14,
 * then widened one card at a time — `venue_maintenance_halt` (a maintenance
 * window just stopped the platform, and it fires while the account-wide
 * cancel is still running), `venue_maintenance_scheduled` (the T-10 that
 * opens the operator's seven minutes), `slack_delivery_failing` (the card
 * that says all the other pages are gone), `counter_ladder_leg_appreciated`
 * (the engine will NOT act on the leg it reports) and, 2026-08-31,
 * `teardown_cancel_failed` — a RESTORATION, since it rang through `error`
 * until the backend split it out.
 *
 * Everything else that used to ring (`counter_failed`,
 * `out_of_balance`, `over_hedged`, `counter_unconfirmed`,
 * `poly_cancel_abandoned`, and — 2026-08-14 — `rule_state_stale` and
 * `recovery_filled` with a `failed`/`no_bid` counter) is now SILENT:
 * still a row, still a colour, just no doorbell — and since 2026-08-31 no
 * Slack post either, for most of them: see the note on `slack_delivery_failing`.
 * None of them loses its last alerting channel by going quiet here.
 *
 * The two 2026-08-14 removals are worth their own line, because both
 * look like obvious ring candidates:
 *   - `rule_state_stale` (the 🛑 "halted" card) is a STATE, read off the
 *     page and the feed rather than reacted to in the moment, and it
 *     fires often enough that ringing it eroded the tone for the alarms
 *     that ARE about money moving.
 *   - `recovery_filled` with `failed`/`no_bid` went silent rather than
 *     down to the fill cue: nothing filled, so the fill cue would say
 *     the opposite of what happened (see `classifyEvent`).
 *
 * Two of the removals were never live to begin with, and the reasons
 * are worth keeping so nobody re-adds them expecting a ring.
 * `platform_disconnect` was routed `['slack']` and ONLY `['slack']`, so
 * it never reached this hook at all — ⚠️ 2026-08-31 it does NOW: Slack
 * became a shortlist of exposure alarms and leaving it at `[]` would
 * have had the dispatcher discard it in silence, so it moved to
 * `['realtime']`. It stays SILENT on its own merits (pod health, one
 * frame per engine pod per platform per stream) and lives in
 * `LIFECYCLE_NOISE_TYPES`, so the feed hides it behind the lifecycle
 * switch. And the three
 * `sniper_*` types (`sniper_fire_blocked`, `sniper_var_reversal`,
 * `sniper_goal_cancelled`) travel on the `/match-events` namespace, not
 * the root `notifications:stream` that feeds this hook — the audible
 * cue on the Sniper page is `useSniperReversalSound` and it is
 * unaffected by anything in this file.
 *
 * The reasoning is alarm fatigue, and it is the whole point of this
 * set. A tone that fires on a dozen different conditions stops being a
 * "stop and look" signal and becomes background noise — at which point
 * the ONE event that genuinely needed eyes is the one that gets
 * ignored. Widening this set back out is not a small change; it costs
 * the meaning of every entry already in it.
 *
 * `emergency_stop` is not here either: panic is operator-initiated, so
 * the doorbell would just confirm a button the operator just pressed.
 */
export const ALARM_EVENT_TYPES = new Set<NotificationEventType>([
  // A generic engine fault — nothing else describes it, so it rings.
  'error',
  // 🚨 A RESTORATION, not a new entry. An order the engine tried to cancel is
  // STILL LIVE at the venue after the verified-cancel retries — unmanaged money
  // only a human can close. It rode `error` until 2026-08-31 and rang through
  // THAT membership; splitting it into its own type on the backend (so it could
  // keep a Slack route of its own when Slack was cut to a shortlist) would have
  // taken the tone away in silence. Bounded by the event: it fires only after
  // the retries are exhausted, which is rare by construction.
  'teardown_cancel_failed',
  // The account-wide watcher's naked-leg net: an automation order filled
  // with NO owning rule / live engine — money moved and nobody is hedging
  // it. Nothing else alerts (scope-aware engines demote the fill), so the
  // operator MUST hear this one.
  'orphaned_automation_fill',
  // A hedge leg resting unfilled while the first leg is already filled —
  // contracts are un-hedged RIGHT NOW. Severity on the wire is `warning`
  // (the engine left the order in place on purpose, it is not a fault),
  // but the money exposure is real and in the terminal case — unhedged
  // quantity AND no `counterStuckMarketHedgeSeconds` window — this card is
  // the LAST thing that will ever be said about the naked leg. That is
  // exactly why it rings despite not being an error: the tone, not the
  // chip colour, is what surfaces it. (2026-08-05 Aarhus/Sabah: 100
  // contracts left naked behind a silent warning.) The ONLY alarm with
  // its own clip and its own lane — see DEDICATED_ALARM_TYPE.
  'counter_stuck',
  // Slack stopped delivering (2026-08-24). This is the one entry whose case
  // is stronger than every other in the set, and it is stronger for a reason
  // that is structural rather than a matter of degree: the predicate that
  // justifies keeping this whitelist short — "the silenced ones stay in the
  // feed AND keep their Slack route, so none loses its last alerting channel
  // by going quiet here" — is exactly FALSE for this card.
  //
  // ⚠️ 2026-08-31: that predicate is now false for ~73 OTHER types too. Slack
  // was cut to a shortlist of ELEVEN exposure alarms (`SLACK_ROUTED_TYPES` in
  // `@arbitrage-core/notifications-core`), so "stays in the feed AND keeps its
  // Slack route" no longer describes the silenced majority — they keep the feed
  // and nothing else. The whitelist stays short anyway, on its own merits: a
  // tone only reaches someone already at the desk with this page open in the
  // sound-leader tab, so widening it buys less than it costs in fatigue.
  // Whoever wants one of those types louder should ask whether it belongs on
  // the Slack shortlist, which is the channel that reaches an absent operator.
  //
  // Back to THIS card: it is routed mongo + realtime and never to Slack (an
  // alarm about Slack sent over Slack cannot arrive), it is not on the bell,
  // and while it is firing EVERY other Slack card is being destroyed on
  // arrival, fills included.
  //
  // So it does not merely deserve a tone; it is the card that says all the
  // other tones and pages are gone. On 2026-08-24 that state lasted ~8 hours
  // and ~1.000 cards were lost. Alarm fatigue is not the risk here: it fires
  // when the alerting channel itself is down, which is rare by construction.
  //
  // What the tone does NOT do — and the honest limit of this entry: it only
  // reaches an operator who already has this page open in a sound-leader tab,
  // and `SOUND_MAX_EVENT_AGE_MS` drops a card older than a minute (modulo the
  // `STALE_DROP_STREAK_LIMIT` recalibration). It shortens the gap for someone
  // at the desk; it does not solve
  // "nobody was looking", which is a paging problem and needs a channel that
  // does not depend on Slack.
  //
  // `slack_budget_warning` and `slack_delivery_recovered` stay QUIET on
  // purpose — the first still delivers (the row is the point, there is time to
  // act) and the second is an all-clear.
  'slack_delivery_failing',
  // A leg hit its max-volume ceiling and stopped operating. The engine is
  // done with it until the operator raises the cap or lets it go, so this
  // one needs ears, not just a row.
  'max_volume_reached',
  // The T-10 heads-up, and the card that starts the operator's SEVEN MINUTES.
  // It rings for exactly that reason: everything after it is either a panic
  // that already ran or a window nobody stopped, and both of those are decided
  // inside a gap that only exists if somebody hears it open. It is bounded to
  // ONE per window, so it cannot become the fatigue this list guards against —
  // and that same boundedness is why it is ALSO cooldown-exempt below: there is
  // no second one to catch.
  'venue_maintenance_scheduled',
  // A venue maintenance window just fired a GLOBAL PANIC on live trading:
  // both engines halted, every resting order being cancelled. The card goes
  // out BEFORE the cancel finishes — that loop runs for tens of seconds
  // against a venue already refusing — so the tone is what tells the
  // operator while it is still happening. Under the
  // auto-panic toggle OFF the SAME card means the opposite (nothing was
  // stopped, the operator is the whole response), which is why it rings
  // either way. The confirmation that follows it
  // (`venue_maintenance_panic`) stays silent: same window, same operator,
  // already looking.
  'venue_maintenance_halt',
  // The counter/hedge ladder's CRITICAL card: the leg we are still exposed
  // on appreciated past the rule's adverse-tick threshold while its hedge
  // was still incomplete. It rings because the engine's own response is to
  // keep laddering the hedge — selling the appreciated leg is a MANUAL
  // action, so nothing happens to that leg until the operator acts. Bounded:
  // the engine latches it once per CYCLE per `(rule, path, makerPlatform)`,
  // and it only fires at a rung deadline, so it stops once the ladder tops
  // out.
  // Its sibling `counter_ladder_step` is deliberately NOT here — that one
  // fires up to twice per cycle on every laddered leg and is exactly the
  // alarm fatigue this list exists to prevent.
  'counter_ladder_leg_appreciated',
]);

/**
 * The ONE alarm that gets its own clip AND its own playback lane —
 * everything else in the whitelist shares the doorbell on the main lane.
 *
 * A lane is a `useAudioPlayer()` instance plus its own cooldown timestamp
 * and stop timer, exactly like the per-platform execution lanes below.
 * `counter_stuck` needs one because a distinct clip is worthless if the
 * shared cooldown can swallow it: the lanes are independent, so a
 * doorbell alarm in the same batch can no longer absorb the naked-leg
 * cue. Both may be audible at once, which is the correct signal — the
 * operator must hear the naked leg regardless of what else went wrong.
 *
 * Deliberately singular, not a map. One dedicated clip earns its keep by
 * standing out against a single background tone; a second would be a
 * vocabulary to memorise, and it could not silently join anyway — a new
 * lane means a new player, a new cooldown ref and a new timer, all wired
 * by hand right here.
 *
 * Clip must be shorter than ALARM_PLAY_MS or the stop timer truncates it
 * (`counter_stuck.mp3` ≈ 1.07 s, `error.mp3` ≈ 1.62 s, both well under).
 */
const DEDICATED_ALARM_TYPE: NotificationEventType = 'counter_stuck';
const DEDICATED_ALARM_SOUND = counterStuckSound;

/**
 * Alarm types that BYPASS `ALARM_COOLDOWN_MS`.
 *
 * The cooldown exists to collapse a FLURRY: several related events inside one
 * window play one tone, because the count belongs in the feed and the tone
 * only says "the lane fired". That reasoning assumes the event repeats — if
 * the tone is swallowed, the next one still arrives.
 *
 * `slack_delivery_failing` breaks that assumption. The engine latches it
 * (`failureAlarmed`) and, while delivery stays broken, emits ONE card — with a
 * quota refusal it fires on the very first refused POST. So a
 * `max_volume_reached` that rang 2.9 s earlier would swallow it, and no repeat
 * is coming to make up for it: the one card that says every OTHER alerting
 * channel is dead would be silent. That is the same failure
 * `DEDICATED_ALARM_TYPE` exists to prevent, except worse, because
 * `counter_stuck` does repeat.
 *
 * The latch is per STREAK, not per incident: a single successful POST clears
 * it, so a flapping condition CAN re-emit. That is exactly why the exemption
 * is a longer window of its own rather than an unbounded bypass — bounding it
 * here does not depend on the backend making a promise it does not make.
 *
 * A window rather than a second dedicated lane: another lane means another
 * clip to memorise, and the doorbell is the right sound for this.
 */
/**
 * Exported for the vocabulary test: widening this set is as dangerous as
 * widening `ALARM_EVENT_TYPES`. A type in here that DOES repeat loses its
 * cooldown for a minute at a time.
 *
 * `venue_maintenance_halt` is a candidate on the same reasoning — the service
 * latches it per window — and is deliberately NOT here yet: its own
 * two-cards-in-one-tick behaviour needs looking at first, and that is a
 * separate change from this one.
 *
 * `venue_maintenance_scheduled` IS here, and for the opposite half of the usual
 * argument. The cooldown assumes the event repeats: swallow one tone and the
 * next arrival still gets through. This card is ONE per window — it opens the
 * seven minutes in which the operator can still decide whether that window
 * stops the platform, and there is no second one. Any other alarm inside the
 * preceding 3s would eat it whole, and the next tone after that is
 * `venue_maintenance_halt` at T−3, by which time the decision is already
 * latched: the whole gap would be lost silently. Keyed BY TYPE below, so its
 * window is its own and it can neither eat nor be eaten by the entry beside it.
 *
 * KNOWN BOUND: by type, not by WINDOW. Two venues announcing inside the same
 * minute — the sweep's second card — leaves the second one mute. Accepted: that
 * card says in its own copy that it gets no decision of its own, so the tone it
 * loses is the one that matters least.
 */
export const COOLDOWN_EXEMPT_ALARM_TYPES: ReadonlySet<NotificationEventType> =
  new Set(['slack_delivery_failing', 'venue_maintenance_scheduled']);

/**
 * Per-platform "play in flight" window. Each fill event plays its
 * platform's clip ONCE (no loop) — subsequent fills arriving inside
 * this window are silently dropped so we don't restart the clip
 * mid-play and produce stutter. Tuned to a comfortable upper bound
 * over the actual clip durations measured with `afinfo`:
 *   - `kalshi-fill.mp3` ≈ 365 ms
 *   - `poly-fill.mp3`   ≈ 648 ms
 * 800 ms covers both with margin. If you swap a longer clip in,
 * bump this. If you swap a shorter clip in, leave it — the throttle
 * only suppresses extra plays, it never delays an idle one.
 *
 * Historical context: this used to be `TAIL_MS = 1500` with
 * `loop: true`, which played the OLD `cash-register.mp3` (~1 s) about
 * 1.5 times per fill — read as one continuous burst when fills
 * stacked. The new shorter clips made a single fill loop 3-4 times
 * (2026-05-23 bug). Switched to one-clip-per-fill + throttle.
 */
const EXECUTION_CLIP_MS = 800;

/**
 * Minimum gap between alarm sounds. Several errors firing within
 * this window play ONE alarm — the page row + Slack post still
 * surface every event, but the audio cue isn't repeated. Tuned
 * short enough that a genuine new error 5 s later still alerts.
 *
 * Counted PER LANE, so the two lanes never absorb each other. Within a
 * lane the tone still says "at least one", never "how many":
 * `PredictionNotificationSound` drains a whole socket batch inside the
 * same millisecond, so a goal on a busy event that puts 6 grouped rules
 * into `counter_stuck` in one engine tick plays the clip ONCE. The count
 * lives in the feed; the audio only says the lane fired.
 *
 * Splitting a lane per TYPE (rather than the current whitelist-wide
 * doorbell lane) would need a player, a timestamp and a stop timer each:
 * keying this window by type alone changes nothing audible, because a
 * second `load()` on one player destroys the howl still playing on it,
 * and this window and ALARM_PLAY_MS are both 3000 ms.
 */
const ALARM_COOLDOWN_MS = 3000;

/**
 * Window for `COOLDOWN_EXEMPT_ALARM_TYPES`, counted on its own ref.
 *
 * Long, because the condition these report is measured in minutes or hours,
 * not seconds: re-ringing every 3 s would make the one tone that means "you
 * are blind everywhere else" into background noise.
 *
 * The blind spot it buys, stated rather than hidden: a SECOND outage that
 * starts within the window is silent. Reachable — a throttle refusal alarms
 * on the streak, one POST succeeds and clears the engine's latch, and a fresh
 * streak 40 s later rings nothing. The card still lands in the feed; only the
 * tone is spent. That trade is deliberate: a tone that fires twice a minute
 * stops being read at all.
 */
const EXEMPT_ALARM_COOLDOWN_MS = 60_000;

/** How long an alarm sound plays before auto-stopping. */
const ALARM_PLAY_MS = 3000;

/**
 * An event older than this never rings, whatever it is.
 *
 * The cue answers "something is happening RIGHT NOW". A stale event cannot
 * mean that, and the socket delivers stale events by design: on every
 * (re)connect the gateway replays from our cursor (`XRANGE`, up to
 * `REPLAY_COUNT_CAP = 5_000` entries), so a laptop that slept, a network
 * blip, a gateway pod restart or a JWT expiry each hand us a burst of fills
 * the operator already handled and alarms already resolved. The
 * `live` flag on the queued frame catches the replay window itself; this
 * catches a stale frame arriving by any other path.
 *
 * Audio ONLY. The row, its colour, the chip and the Slack post are untouched
 * — nothing loses its last alerting channel by going quiet here.
 */
const SOUND_MAX_EVENT_AGE_MS = 60_000;

/**
 * After this many CONSECUTIVE events dropped for being stale, assume the
 * clock reference is wrong rather than the events, recalibrate, and let the
 * event through.
 *
 * A workstation clock that jumps forward mid-session (a VM, a resume from
 * suspend before NTP re-syncs) makes every event look old, and an age gate
 * with no escape hatch would mute `counter_stuck`,
 * `orphaned_automation_fill` and every other alarm — permanently, with no
 * toast and no error. That is fail-CLOSED on the one path that must fail
 * OPEN, so the streak is a hard ceiling on how much this gate can swallow.
 *
 * CONSECUTIVE is the right shape only because the clock is `streamId` — one
 * clock for every producer. It was the wrong shape while the gate read
 * `occurredAt`: with a dozen pod clocks, one skewed pod's events were dropped
 * forever while healthy traffic from the others reset the streak on every
 * other frame, so the ceiling never fired at all.
 */
const STALE_DROP_STREAK_LIMIT = 25;

/**
 * How many recently-played event ids to remember.
 *
 * This used to be a single slot (`lastPlayedId`), which only ever dropped the
 * IMMEDIATELY REPEATED event — while the socket enqueues duplicates ON
 * PURPOSE: a frame whose `streamId` is not strictly greater than the cursor
 * is a replay overlapping live, and those exist because the gateway joins the
 * broadcast room BEFORE it runs the replay. With one slot, the same event
 * sandwiched between two others rang twice.
 *
 * Bounded memory, not a time window: an id evicted from here is already
 * silent by age (SOUND_MAX_EVENT_AGE_MS).
 */
const PLAYED_ID_CAP = 1000;

/**
 * One operation, one ring.
 *
 * A single fill produces several audible events: per-chunk partials plus the
 * final one (a manual order filling in three chunks is three
 * `manual_order_partial_fill` + one `manual_order_executed` = four cues), and
 * the 800 ms in-flight guard below only swallows the chunks that land close
 * together. Within this window, the FIRST fill of a given order/rule on a
 * given venue rings and the rest stay quiet; a genuinely new fill after it
 * rings again.
 *
 * Keyed by (order or rule, venue) — never by venue alone, or two rules
 * filling at once would collapse into one cue and the operator would hear
 * strictly less than what happened.
 */
const EXECUTION_COLLAPSE_MS = 3000;

/** Drop collapse keys older than this so the map can't grow unbounded. */
const EXECUTION_COLLAPSE_PRUNE_MS = 60_000;

type Tier = 'execution' | 'alarm';
type ExecutionPlatform = 'kalshi' | 'polymarket';

/** Per-call context the caller knows and the event itself does not carry. */
export interface PlaySoundOptions {
  /**
   * `false` ⇒ the frame arrived inside a cursor-replay window (see
   * `QueuedNotification` in `useNotificationsSocket`) and must stay SILENT.
   * Defaults to `true` so a caller that holds a single live event does not
   * have to think about it.
   */
  live?: boolean;
}

/**
 * Surface an audio-load failure instead of swallowing it silently. The
 * old `catch {}` hid a broken `load(...)` so the operator lost the
 * audible fill/alarm cue with no signal at all. Every failure goes to
 * Sentry; the operator toast fires ONCE (ref-latched, per mount) so a
 * persistently-broken audio context doesn't spam the UI. The latch is a ref
 * and not module state for the same reason as the one above: module state
 * leaks between tests and makes the "only once" assertion vacuous.
 */
function reportSoundLoadFailure(
  err: unknown,
  toastShown: { current: boolean }
): void {
  Sentry.captureException(
    err instanceof Error ? err : new Error('notification sound load failed'),
    { tags: { area: 'notification-sound' } },
  );
  if (!toastShown.current) {
    toastShown.current = true;
    toast.error('Notification sounds unavailable — check audio permissions.', {
      toastId: 'notification-sound-load',
    });
  }
}

/**
 * Milliseconds out of a Redis Stream entry id (`"<ms>-<seq>"`), or `null`.
 */
function streamEntryMs(streamId: string | null | undefined): number | null {
  if (typeof streamId !== 'string') return null;
  const dash = streamId.indexOf('-');
  const ms = Number(dash === -1 ? streamId : streamId.slice(0, dash));
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/**
 * Age of an event in ms, or `null` when it cannot be established — in which
 * case it RINGS. Fail towards sound, never towards silence.
 *
 * 🚨 THE CLOCK IS `streamId`, NOT `occurredAt`, and that is the whole point.
 * `occurredAt` is stamped by whichever pod dispatched — the per-group opener
 * feed, the unwinder, manual-order-monitor, the sniper — so it is a dozen
 * different clocks compared against a thirteenth, the operator's workstation.
 * With a per-session baseline that mixture is unfixable: anchor it on a pod
 * running fast and every OTHER pod's events look old forever. Measured:
 * `orphaned_automation_fill` comes from a single pod, so a three-minute skew
 * on `manual-order-monitor` alone silenced it permanently, and the streak
 * ceiling never fired because healthy traffic from other pods kept resetting
 * it. `streamId` is the Redis Stream entry id — ONE clock, the same one for
 * every producer, because every event goes through that stream on its way
 * here. What is left to correct for is a single offset, which is what the
 * baseline below does.
 *
 * The baseline is the SMALLEST `now - streamMs` seen on a LIVE frame this
 * session: an event cannot reach us faster than the minimum, so anything
 * below it is offset, not age. It is CLAMPED AT ZERO so a future-stamped
 * entry cannot drag it negative — unclamped, one such frame became the
 * permanent baseline and muted 24 consecutive `counter_stuck` alarms.
 * Clamped and live-only, the baseline lives in `[0, true minimum lag]`, so the
 * correction can only ever make an event look YOUNGER than a raw comparison
 * would. This gate can be too permissive; it can never invent staleness.
 *
 * No `streamId` ⇒ no age gate. Only the legacy pub/sub transport omits it,
 * and that transport has no cursor replay to burst in the first place.
 */
function ageForSound(
  event: NotificationEvent,
  now: number,
  minLag: { current: number },
  live: boolean
): number | null {
  const at = streamEntryMs(event.streamId);
  if (at === null) return null;
  const lag = now - at;
  // Only a LIVE frame may set the baseline. A replayed one is known-old by
  // definition, and letting it calibrate is how the gate turned itself off
  // mid-replay: the streak escape hatch resets the baseline, the very next
  // replayed frame re-anchors it on its own hours-old timestamp, and every
  // remaining frame in that burst then measures as brand new.
  if (live && lag >= 0 && lag < minLag.current) minLag.current = lag;
  const baseline = Number.isFinite(minLag.current) ? minLag.current : 0;
  return lag - baseline;
}

/**
 * The identity a fill cue collapses on: the ORDER it belongs to, falling back
 * to the rule, then the pair.
 *
 * `orderId` is on the payload of every manual fill
 * (`ManualFillPayload.orderId`); `ruleId` is on the envelope of every engine
 * event.
 *
 * `null` ⇒ DO NOT COLLAPSE. An event carrying none of the three cannot be
 * tied to an operation, and inventing an identity for it (the event's own id,
 * say) is worse than useless: it makes every anonymous fill collapse against
 * ITSELF, which silently swallows a genuine re-delivery of the same id and
 * hides the played-id set behind a window it has nothing to do with.
 */
function executionCollapseKey(
  event: NotificationEvent,
  platform: ExecutionPlatform
): string | null {
  const orderId = (event.payload as { orderId?: unknown } | undefined)?.orderId;
  const owner =
    (typeof orderId === 'string' && orderId) || event.ruleId || event.pairId;
  return owner ? `${owner}:${platform}` : null;
}

/**
 * The age gate dropped a whole streak in a row — almost certainly a skewed
 * workstation clock rather than a feed replaying the past. Reported ONCE, and
 * the latch is a REF rather than module state: the sound mount lives for the
 * whole session either way, so the behaviour is the same, but a module-level
 * flag leaks across tests and made the assertion on this pass because an
 * EARLIER test had already fired it.
 */
function reportStaleGateRunaway(
  age: number,
  reported: { current: boolean }
): void {
  if (reported.current) return;
  reported.current = true;
  Sentry.captureMessage(
    'notification sound age gate dropped a full streak — check the workstation clock',
    { level: 'warning', tags: { area: 'notification-sound' }, extra: { age } }
  );
}

function classifyEvent(event: NotificationEvent): Tier | null {
  if (ALARM_EVENT_TYPES.has(event.type)) return 'alarm';
  if (event.type === 'recovery_filled') {
    // Recovery "executing" = automation is hedging the leg, no operator
    // action needed → execution tier (fill cue). Anything else ("failed"
    // / "no_bid") is SILENT: it rang until 2026-08-14 and the operator
    // dropped it, and the fill cue would be a lie — nothing filled. The
    // row, colour and Slack post still carry it.
    const counterAction = (event.payload as any)?.counterAction;
    return counterAction === 'executing' ? 'execution' : null;
  }
  if (EXECUTION_EVENT_TYPES.has(event.type)) return 'execution';
  return null;
}

/**
 * Resolve the platform an execution event is about, so we can route
 * to the right per-platform cash-register loop. Reads `event.platform`
 * first (set by the producer on every fill notification — see
 * `slack-notification.service.ts`'s `notifyMakerFilled` /
 * `notifyCounterFilled` / `notifyCrossingExecuted` etc.), and falls
 * back to payload fields for legacy events that didn't thread it
 * through. Returns `null` for events with no resolvable platform
 * (notably `execution_complete`) — the caller
 * extends whichever player is already running instead of starting
 * one arbitrarily, so a cycle-end summary on its own stays silent.
 */
function resolveExecutionPlatform(
  event: NotificationEvent
): ExecutionPlatform | null {
  const candidates: unknown[] = [
    event.platform,
    (event.payload as any)?.platform,
    (event.payload as any)?.counterPlatform,
    (event.payload as any)?.makerPlatform,
    // Sniper frames carry the target as `venue` (kalshi|polymarket).
    (event.payload as any)?.venue
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const lc = c.toLowerCase();
    if (lc === 'kalshi' || lc === 'polymarket') return lc;
  }
  return null;
}

/**
 * Returns a STABLE `play(event)` — a per-platform fill clip for execution
 * events and a doorbell / naked-leg alarm for the whitelist. Designed
 * around the operator's reality: a quiet engine produces few sounds, a
 * busy cycle produces one clip per platform, and any alarm that genuinely
 * needs eyes punches through.
 *
 * IMPERATIVE ON PURPOSE. The caller drains a whole socket batch and calls
 * this once per event, in a plain loop. Feeding a batch through React
 * state instead LOSES EVENTS: `setState` per event coalesces into one
 * render, so only the last value of the batch survives — a `counter_stuck`
 * with anything after it in the same burst never rang at all, which no
 * amount of per-lane cooldown work can fix. Every dedupe/cooldown decision
 * lives inside this callback, so calling it in a tight loop is correct: the
 * played-id set still drops a repeat, and each lane still collapses its own
 * burst.
 *
 * Four gates run before any tier logic, in this order — a frame that fails
 * one is SILENT, never a missing row:
 *   1. `options.live === false` — the frame came from a cursor replay.
 *   2. `occurredAt` older than SOUND_MAX_EVENT_AGE_MS (unreadable ⇒ rings).
 *   3. the event id is already in the bounded played-id set.
 *   4. the type is in neither tier set.
 * The caller adds two more of its own before ever calling here: the
 * notifications route must be open in this tab, and this tab must hold the
 * cross-tab sound-leader lock (see `PredictionNotificationSound`).
 */
export function useNotificationSoundPlayer(): (
  event: NotificationEvent,
  options?: PlaySoundOptions
) => void {
  // Main player carries the shared doorbell; `counter_stuck` gets its
  // own (see DEDICATED_ALARM_TYPE) so the two alarm lanes can overlap
  // instead of absorbing each other. Execution gets its own
  // per-platform players below so a Kalshi fill loop and a Poly fill
  // loop can overlap without fighting over a single audio handle.
  //
  // One player per concurrent sound is not a style choice: a second
  // `load()` on a player destroys the howl currently attached to it
  // (`createHowl` → `destroyHowl` → `unload`), so two clips sharing a
  // player can never sound together.
  //
  // IMPORTANT: `useAudioPlayer()` returns a NEW wrapper object every
  // render (it spreads internal `state` into the return value, and
  // state updates fire on every audio event). The INNER `load`/`stop`
  // callbacks are stable (wrapped in `useCallback([])` inside the
  // lib), so we destructure them up front and use only those stable
  // refs in the effect dep arrays. Storing the wrapper objects
  // directly silently stopped every sound on each render because the
  // cleanup effect's dep changed each tick.
  const { load: loadMain, stop: stopMain } = useAudioPlayer();
  const { load: loadDedicated, stop: stopDedicated } = useAudioPlayer();
  const { load: loadKalshi, stop: stopKalshi } = useAudioPlayer();
  const { load: loadPoly, stop: stopPoly } = useAudioPlayer();
  // Bounded, insertion-ordered set of ids already considered for playback.
  // Insertion order is what makes eviction FIFO — `Set` iterates in insertion
  // order, so the first value is always the oldest.
  const playedIds = useRef<Set<string>>(new Set());

  // Last time each (order/rule, venue) fill cue was allowed through. Bounded
  // by pruning on every execution play.
  const executionCollapseAt = useRef<Map<string, number>>(new Map());

  // Smallest `Date.now() - occurredAt` seen this session (backend↔browser
  // clock offset + minimum delivery latency), and how many events in a row
  // the age gate has dropped. See ageForSound / STALE_DROP_STREAK_LIMIT.
  const minEventLag = useRef(Number.POSITIVE_INFINITY);
  const staleDropStreak = useRef(0);

  // One-shot report latches (see the two report* helpers).
  const audioLoadToastShown = useRef(false);
  const staleGateReported = useRef(false);

  // Set while EITHER alarm lane is playing, so an execution loop
  // doesn't clobber an in-progress alarm and a stale event re-render
  // doesn't restart an unrelated sound. The per-platform execution
  // players track their own burst state via the *StartedAt refs
  // below, so they don't need to read this.
  const currentTier = useRef<Tier | null>(null);

  // Per-platform "play in flight until" timestamp (Date.now() ms).
  // 0 = idle. Set on each load() to `now + EXECUTION_CLIP_MS` and
  // checked before starting a new play so rapid bursts don't restart
  // the clip mid-play (stutter). Reset to 0 by `stopAllExecution()`
  // when an alarm cuts the play short.
  const kalshiPlayingUntil = useRef(0);
  const polyPlayingUntil = useRef(0);

  // Alarm cooldown + stop timer, PER LANE. Keeping them separate is what
  // stops a doorbell alarm from swallowing a `counter_stuck` that lands
  // in the same drained batch.
  // `-Infinity`, not 0, for "this lane has never fired". Under `Date.now()`
  // zero was a harmless sentinel because every real stamp was enormous; under
  // `performance.now()` it is a stamp from a second ago, so `now - 0 <
  // ALARM_COOLDOWN_MS` and the FIRST alarm of the session — a `counter_stuck`
  // included — was swallowed by a cooldown that had never run.
  const lastAlarmAt = useRef(Number.NEGATIVE_INFINITY);
  const lastDedicatedAlarmAt = useRef(Number.NEGATIVE_INFINITY);
  /**
   * Own window for `COOLDOWN_EXEMPT_ALARM_TYPES`, keyed BY TYPE.
   *
   * A single shared timestamp would put the exempt types back in the position
   * the exemption exists to fix — one muting another, and for 60 s rather
   * than 3. Inert while the set has one member, and a trap the moment the
   * second one lands (the set's own doc names a candidate).
   */
  const lastExemptAlarmAt = useRef(new Map<NotificationEventType, number>());
  const mainStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dedicatedStopTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const play = useCallback((
    event: NotificationEvent,
    options?: PlaySoundOptions
  ): void => {
    if (!event) return;
    // TWO CLOCKS, on purpose. The age gate compares against the `streamId` ms,
    // which is Redis's WALL clock, so it needs ours. Everything else on this
    // path measures a DURATION — how long since this lane last rang, since
    // this operation last rang, since this clip started — and a duration on a
    // wall clock breaks backwards: a laptop or VM resuming before NTP
    // re-syncs makes `now - lastAlarmAt` NEGATIVE, which reads as "inside the
    // cooldown" and mutes every alarm, `counter_stuck` included, for as long
    // as the skew lasts. Silently.
    const nowWall = Date.now();
    const now = performance.now();
    const tier = classifyEvent(event);
    if (!tier) return;

    if (playedIds.current.has(event.id)) return;
    // Recorded BEFORE the mute gates below, not after. An event silenced for
    // being replay or stale has still been SEEN, and the same id really does
    // come round again: the gateway runs one full cursor replay per
    // `subscribe_notifications`, so a re-delivery lands outside the replay
    // window and would ring as if it were live. Remembering only what we
    // played would leave that hole open. Only ids that reach a tier are
    // recorded, so a quiet lifecycle burst can't evict the real ones.
    playedIds.current.add(event.id);
    if (playedIds.current.size > PLAYED_ID_CAP) {
      const oldest = playedIds.current.values().next().value;
      if (oldest !== undefined) playedIds.current.delete(oldest);
    }

    // Replay/backfill silences FILLS, never alarms.
    //
    // A replayed fill is noise: the operator already saw it. A replayed ALARM
    // is not — the gateway joins the broadcast room BEFORE it runs the cursor
    // replay, so an alarm that fires during a thirty-second blip comes back
    // tagged as replay, and muting it would mean two rules going
    // `counter_stuck` (contracts un-hedged RIGHT NOW) produced no cue at all.
    // What keeps that from becoming a storm is the age gate below — sixty
    // seconds is the whole window in which a replayed alarm is still about
    // money moving now — plus the per-lane cooldown and the played-id set
    // above, so a burst of them is still ONE clip.
    if (options?.live === false && tier !== 'alarm') return;

    const age = ageForSound(event, nowWall, minEventLag, options?.live !== false);
    // `null` = no usable clock ⇒ RING. Fail towards sound, never towards
    // silence: swallowing a `counter_stuck` (contracts un-hedged right now)
    // over a missing stream id is far worse than one stale ding.
    if (age !== null && age > SOUND_MAX_EVENT_AGE_MS) {
      staleDropStreak.current += 1;
      if (staleDropStreak.current < STALE_DROP_STREAK_LIMIT) return;
      // The gate has swallowed a whole streak — the clock reference, not the
      // feed, is what is wrong. Recalibrate, report once, and let this one
      // through rather than keep muting.
      staleDropStreak.current = 0;
      minEventLag.current = Number.POSITIVE_INFINITY;
      reportStaleGateRunaway(age, staleGateReported);
    } else {
      staleDropStreak.current = 0;
    }

    // Helpers — inlined so the callback's dep array stays accurate
    // (every player callback these read is one it depends on).
    const stopAllExecution = (): void => {
      kalshiPlayingUntil.current = 0;
      stopKalshi();
      polyPlayingUntil.current = 0;
      stopPoly();
    };

    // The tier is only idle once BOTH lanes are done — clearing it off
    // whichever timer happens to fire first would let a fill cue slip in
    // under an alarm still playing on the other lane.
    const clearTierIfNoLanePlaying = (): void => {
      if (!mainStopTimer.current && !dedicatedStopTimer.current) {
        currentTier.current = null;
      }
    };

    if (tier === 'alarm') {
      const dedicated = event.type === DEDICATED_ALARM_TYPE;
      const lastAt = dedicated ? lastDedicatedAlarmAt : lastAlarmAt;
      const stopTimer = dedicated ? dedicatedStopTimer : mainStopTimer;
      const load = dedicated ? loadDedicated : loadMain;
      const stop = dedicated ? stopDedicated : stopMain;

      // Cooldown is per lane, so this only ever silences a REPEAT of the same
      // lane — never the other alarm.
      //
      // Exempt types keep their OWN, much longer window instead of bypassing
      // outright, and — critically — they do NOT stamp the shared one. Both
      // halves matter:
      //
      //   - Own window: the tone must never be swallowed by an unrelated
      //     alarm that rang 2.9 s ago, but it also must not become a siren if
      //     the underlying condition flaps.
      //   - Not stamping the shared window: the previous version advanced it
      //     on every exempt event, which handed the swallow to the NEIGHBOURS
      //     (`error`, `orphaned_automation_fill`, `max_volume_reached`,
      //     `venue_maintenance_halt`) — moving the bug one seat over rather
      //     than fixing it. Before the exemption existed the suppressed event
      //     returned WITHOUT stamping, which is the behaviour restored here.
      const exempt = COOLDOWN_EXEMPT_ALARM_TYPES.has(event.type);
      if (exempt) {
        const prev =
          lastExemptAlarmAt.current.get(event.type) ?? Number.NEGATIVE_INFINITY;
        if (now - prev < EXEMPT_ALARM_COOLDOWN_MS) return;
        lastExemptAlarmAt.current.set(event.type, now);
      } else {
        if (now - lastAt.current < ALARM_COOLDOWN_MS) return;
        lastAt.current = now;
      }

      // Interrupt any execution loop in progress — alarms have
      // priority. Operator should hear the doorbell over a running
      // fill loop.
      stopAllExecution();
      if (stopTimer.current) clearTimeout(stopTimer.current);
      currentTier.current = 'alarm';
      try {
        load(dedicated ? DEDICATED_ALARM_SOUND : alarmSound, {
          autoplay: true,
          loop: false,
        });
      } catch (err) {
        reportSoundLoadFailure(err, audioLoadToastShown);
        stopTimer.current = null;
        clearTierIfNoLanePlaying();
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        stopTimer.current = null;
        clearTierIfNoLanePlaying();
      }, ALARM_PLAY_MS);
      return;
    }

    // tier === 'execution' — defer only to alarms.
    if (currentTier.current === 'alarm') return;

    // One discrete play per fill event (no loop). The per-platform
    // `playingUntil` guard skips additional fills arriving while the
    // clip is still in flight, so we never restart the clip mid-play.
    // Inlined (rather than a separate helper) so the dep array stays
    // accurate — every player + ref read here is one this callback
    // already depends on.
    const playExecutionFill = (
      platform: ExecutionPlatform,
      tNow: number
    ): void => {
      // One operation, one ring: the partials and the final fill of the same
      // order/rule on the same venue collapse into the first cue. An event we
      // cannot tie to an operation has no key and is never collapsed.
      const collapseKey = executionCollapseKey(event, platform);
      if (collapseKey !== null) {
        const collapsedAt = executionCollapseAt.current.get(collapseKey);
        if (
          collapsedAt !== undefined &&
          tNow - collapsedAt < EXECUTION_COLLAPSE_MS
        ) {
          return;
        }
        for (const [key, at] of executionCollapseAt.current) {
          if (tNow - at > EXECUTION_COLLAPSE_PRUNE_MS) {
            executionCollapseAt.current.delete(key);
          }
        }
        // Stamped BEFORE the in-flight guard on purpose. That guard only fires
        // while this venue's clip is audibly playing, so the operator did hear
        // "venue X filled" — letting the same key ring again 800 ms later
        // would reintroduce exactly the repetition this window exists to
        // remove.
        executionCollapseAt.current.set(collapseKey, tNow);
      }

      const playingUntil =
        platform === 'kalshi' ? kalshiPlayingUntil : polyPlayingUntil;
      if (tNow < playingUntil.current) return;
      const load = platform === 'kalshi' ? loadKalshi : loadPoly;
      const src = platform === 'kalshi' ? kalshiFillSound : polyFillSound;
      try {
        load(src, { autoplay: true, loop: false });
      } catch (err) {
        reportSoundLoadFailure(err, audioLoadToastShown);
        return;
      }
      playingUntil.current = tNow + EXECUTION_CLIP_MS;
    };

    // Cycle-end summaries (`execution_complete`)
    // have no resolvable platform — the maker/counter fills inside
    // the cycle already triggered their own clip plays, so the
    // summary on its own stays silent.
    const platform = resolveExecutionPlatform(event);
    if (platform === null) return;

    playExecutionFill(platform, now);
  }, [
    loadMain,
    stopMain,
    loadDedicated,
    stopDedicated,
    loadKalshi,
    stopKalshi,
    loadPoly,
    stopPoly,
  ]);

  // Cleanup on unmount — stop every player so no audio handle
  // outlives the page navigation. Deps are the stable `stop`
  // callbacks (NOT the player wrapper objects) so this only
  // fires on unmount, not on every render.
  useEffect(() => {
    return () => {
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      if (dedicatedStopTimer.current) {
        clearTimeout(dedicatedStopTimer.current);
      }
      // Null the refs, not just the timeouts: a non-null timer ref means
      // "this lane is still playing" to `clearTierIfNoLanePlaying`, so
      // leaving them set would wedge `currentTier` at 'alarm' and mute
      // every fill for the rest of the session if this cleanup ever ran
      // with the component still alive.
      mainStopTimer.current = null;
      dedicatedStopTimer.current = null;
      currentTier.current = null;
      stopMain();
      stopDedicated();
      stopKalshi();
      stopPoly();
    };
  }, [stopMain, stopDedicated, stopKalshi, stopPoly]);

  return play;
}
